import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '../../../hooks/useClickOutside';
import type { StatusFilterDropdownProps } from '../../../types/statusFilterDropdownTypes';

function StatusFilterDropdownInner<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  iconName = 'filter_list',
  placeholder,
}: StatusFilterDropdownProps<T>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(ref, close, isOpen);

  const selected = options.find((opt) => opt.value === value) ?? options[0];
  const rawLabel = selected?.label ?? placeholder ?? '';
  const label = rawLabel ? t(rawLabel) : '';

  const handleSelect = useCallback(
    (next: T | '') => {
      onChange(next);
      setIsOpen(false);
    },
    [onChange],
  );

  return (
    <div className="status-filter-wrapper" ref={ref}>
      <button
        type="button"
        className={`status-filter-btn ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${ariaLabel}: ${label}`}
      >
        <span className="status-filter-icon-left" aria-hidden="true">
          <span className="material-symbols-outlined">{iconName}</span>
        </span>
        <span>{label}</span>
        <span className="status-filter-icon-right" aria-hidden="true">
          <span className="material-symbols-outlined">expand_more</span>
        </span>
      </button>

      {isOpen && (
        <div className="status-filter-dropdown" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => {
            const isSelected = value === option.value;
            return (
              <button
                key={option.value || '__all__'}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`status-filter-option ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelect(option.value as T | '')}
              >
                <span className={`status-option-dot ${option.dotClass}`} aria-hidden="true" />
                <span>{t(option.label)}</span>
                {isSelected && (
                  <span className="status-option-check material-symbols-outlined" aria-hidden="true">
                    check
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const StatusFilterDropdown = memo(StatusFilterDropdownInner) as <T extends string>(
  props: StatusFilterDropdownProps<T>,
) => ReturnType<typeof StatusFilterDropdownInner>;
